import { useEffect, useMemo, useState } from "react";
import client from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Printer } from "@phosphor-icons/react";

export default function PrintBoxLabel() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [details, setDetails] = useState("");

  useEffect(() => { (async () => { const { data } = await client.get("/companies"); setCompanies(data); })(); }, []);
  const company = useMemo(() => companies.find(c => c.id === companyId), [companies, companyId]);

  const today = useMemo(() => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth()+1).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()}`;
  }, []);

  const canPrint = !!company && details.trim().length > 0;

  return (
    <div>
      <PageHeader title="Print Box Label" subtitle="⅓ A4 · Company · Details · Date" />
      <Section>
        <div className="screen-only bg-white border border-slate-200 rounded-sm p-6 max-w-xl">
          <div className="grid gap-4">
            <div>
              <label className="label-cap">Company</label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger data-testid="pbl-company-select" className="rounded-sm"><SelectValue placeholder="Select company"/></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Details (multi-line)</label>
              <Textarea data-testid="pbl-details-input" rows={5} value={details} onChange={(e)=>setDetails(e.target.value)} placeholder="Freeform text to print at the centre of the box label"/>
            </div>
            <Button data-testid="pbl-print-btn" onClick={()=>window.print()} disabled={!canPrint} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm w-fit">
              <Printer size={16} className="mr-1" weight="bold"/> Print
            </Button>
          </div>

          {canPrint && (
            <div className="mt-8">
              <div className="label-cap mb-2">Preview</div>
              <div className="pbl-preview shadow-md">
                <BoxLabelBody companyName={company.name} details={details} date={today} />
              </div>
            </div>
          )}
        </div>

        <div className="print-area" id="pbl-print-area">
          {canPrint && <div className="a4-third-page"><BoxLabelBody companyName={company.name} details={details} date={today}/></div>}
        </div>

        <style>{`
          .pbl-preview { width: 210mm; height: 99mm; background: white; border: 1px solid #d4d4d8; max-width: 100%; transform-origin: top left; }
          .a4-third-page { width: 210mm; height: 99mm; background: white; box-sizing: border-box; overflow: hidden; }
          @media screen {
            .print-area { display: none; }
            @media (max-width: 900px) { .pbl-preview { transform: scale(0.45); } }
          }
          @media print {
            @page { size: 210mm 297mm; margin: 0; }
            html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
            body * { visibility: hidden !important; }
            #pbl-print-area, #pbl-print-area * { visibility: visible !important; }
            #pbl-print-area { position: absolute; left: 0; top: 0; width: 210mm; display: block !important; }
          }
        `}</style>
      </Section>
    </div>
  );
}

function BoxLabelBody({ companyName, details, date }) {
  const black = { color: "#000" };
  return (
    <div style={{
      width: "100%", height: "100%", padding: "12mm",
      display: "flex", flexDirection: "column", justifyContent: "space-between",
      border: "3px solid #000", boxSizing: "border-box",
      fontFamily: "'IBM Plex Sans', system-ui, sans-serif", ...black,
    }}>
      <div style={{ textAlign: "center", fontSize: "14mm", fontWeight: 900, letterSpacing: "-0.01em", lineHeight: 1.1, ...black }}>
        {companyName}
      </div>
      <div style={{ textAlign: "center", fontSize: "9mm", fontWeight: 900, whiteSpace: "pre-wrap", lineHeight: 1.2, ...black }}>
        {details}
      </div>
      <div style={{ textAlign: "center", fontSize: "8mm", fontWeight: 900, fontFamily: "'IBM Plex Mono', monospace", ...black }}>
        {date}
      </div>
    </div>
  );
}
